import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Switch,
  FormControlLabel, Alert, CircularProgress, Divider, Chip,
  Accordion, AccordionSummary, AccordionDetails, Table, TableHead,
  TableRow, TableCell, TableBody, Stack,
  FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab
} from '@mui/material';
import {
  Save, Send, Inbox, CheckCircle, Error as ErrorIcon, ExpandMore, Mail,
  CreditCard, Bolt, QrCode2, CloudUpload, Delete, SmartToy
} from '@mui/icons-material';
import api from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingOut, setTestingOut] = useState(false);
  const [testingIn, setTestingIn] = useState(false);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ message: '', severity: 'info' });
  const [outResult, setOutResult] = useState(null);
  const [inResult, setInResult] = useState(null);
  const [testRecipient, setTestRecipient] = useState('');

  // ─── Payment Gateway state ─────────────────────────────────────────────
  const [pg, setPg] = useState(null);
  const [savingPg, setSavingPg] = useState(false);
  const [testingPg, setTestingPg] = useState(false);
  const [pgResult, setPgResult] = useState(null);
  const [uploadingAlipayQr, setUploadingAlipayQr] = useState(false);
  const [uploadingWechatQr, setUploadingWechatQr] = useState(false);

  // ─── AI (Claude API key) state ─────────────────────────────────────────
  const [ai, setAi] = useState(null);
  const [savingAi, setSavingAi] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  // Active tab (0 = Email, 1 = Payment Gateway, 2 = AI Assistant)
  const [tab, setTab] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      const e = data.data.email;
      const p = data.data.paymentGateway || {};
      setPg({
        provider: p.provider || 'manual',
        enabled: !!p.enabled,
        mode: p.mode || 'sandbox',
        currency: p.currency || 'USD',
        stripePublishableKey: p.stripePublishableKey || '',
        stripeSecretKey: p.stripeSecretKeyMasked || '',
        stripeWebhookSecret: p.stripeWebhookSecretMasked || '',
        paypalClientId: p.paypalClientId || '',
        paypalClientSecret: p.paypalClientSecretMasked || '',
        razorpayKeyId: p.razorpayKeyId || '',
        razorpayKeySecret: p.razorpayKeySecretMasked || '',
        alipayQrUrl: p.alipayQrUrl || '',
        alipayAccountName: p.alipayAccountName || '',
        wechatQrUrl: p.wechatQrUrl || '',
        wechatAccountName: p.wechatAccountName || '',
        stripeSecretKeySet: !!p.stripeSecretKeySet,
        stripeWebhookSecretSet: !!p.stripeWebhookSecretSet,
        paypalClientSecretSet: !!p.paypalClientSecretSet,
        razorpayKeySecretSet: !!p.razorpayKeySecretSet,
        lastTestAt: p.lastTestAt,
        lastTestOk: p.lastTestOk,
        lastTestError: p.lastTestError,
        lastTestProvider: p.lastTestProvider
      });
      const aiData = data.data.ai || {};
      setAi({
        claudeApiKey: aiData.claudeApiKeyMasked || '',
        claudeApiKeySet: !!aiData.claudeApiKeySet,
        lastTestAt: aiData.lastTestAt,
        lastTestOk: aiData.lastTestOk,
        lastTestError: aiData.lastTestError
      });
      setForm({
        smtpHost: e.smtpHost, smtpPort: e.smtpPort, smtpSecure: e.smtpSecure,
        smtpUser: e.smtpUser, smtpPass: e.smtpPassMasked,
        imapHost: e.imapHost, imapPort: e.imapPort, imapSecure: e.imapSecure,
        imapUser: e.imapUser, imapPass: e.imapPassMasked,
        fromEmail: e.fromEmail, fromName: e.fromName,
        adminEmail: e.adminEmail, enabled: e.enabled,
        lastOutgoingTestAt: e.lastOutgoingTestAt, lastOutgoingTestOk: e.lastOutgoingTestOk, lastOutgoingTestError: e.lastOutgoingTestError,
        lastIncomingTestAt: e.lastIncomingTestAt, lastIncomingTestOk: e.lastIncomingTestOk, lastIncomingTestError: e.lastIncomingTestError,
        smtpPassSet: e.smtpPassSet, imapPassSet: e.imapPassSet
      });
    } catch (err) {
      setStatus({ message: err.response?.data?.error || err.message, severity: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: v }));
  };

  const applyGmailPreset = () => {
    setForm((p) => ({
      ...p,
      smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpSecure: false,
      imapHost: 'imap.gmail.com', imapPort: 993, imapSecure: true
    }));
    setStatus({ message: 'Gmail preset applied. For Gmail use a 16-character App Password (https://myaccount.google.com/apppasswords).', severity: 'info' });
  };

  const save = async () => {
    setSaving(true);
    setStatus({ message: '', severity: 'info' });
    try {
      const payload = { ...form };
      // Don't send masked passwords back
      if (payload.smtpPass && payload.smtpPass.includes('•')) delete payload.smtpPass;
      if (payload.imapPass && payload.imapPass.includes('•')) delete payload.imapPass;
      await api.put('/admin/settings/email', payload);
      setStatus({ message: 'Email settings saved.', severity: 'success' });
      await load();
    } catch (err) {
      setStatus({ message: err.response?.data?.error || err.message, severity: 'error' });
    }
    setSaving(false);
  };

  const testOutgoing = async () => {
    setTestingOut(true); setOutResult(null);
    try {
      const { data } = await api.post('/admin/settings/email/test-outgoing', { to: testRecipient || undefined });
      setOutResult({ ok: true, ...data });
    } catch (err) {
      setOutResult({ ok: false, error: err.response?.data?.error || err.message });
    }
    setTestingOut(false);
    load();
  };

  const testIncoming = async () => {    setTestingIn(true); setInResult(null);
    try {
      const { data } = await api.post('/admin/settings/email/test-incoming', {});
      setInResult({ ok: true, ...data });
    } catch (err) {
      setInResult({ ok: false, error: err.response?.data?.error || err.message });
    }
    setTestingIn(false);
    load();
  };

  // ─── Payment Gateway handlers ─────────────────────────────────────────
  const handlePgChange = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setPg((prev) => ({ ...prev, [field]: v }));
  };

  const savePaymentGateway = async () => {
    setSavingPg(true);
    setStatus({ message: '', severity: 'info' });
    try {
      const payload = { ...pg };
      // Strip masked secrets so they aren't overwritten with bullets
      ['stripeSecretKey', 'stripeWebhookSecret', 'paypalClientSecret', 'razorpayKeySecret'].forEach((k) => {
        if (payload[k] && payload[k].includes('•')) delete payload[k];
      });
      // Strip read-only diagnostic fields
      ['lastTestAt', 'lastTestOk', 'lastTestError', 'lastTestProvider',
       'stripeSecretKeySet', 'stripeWebhookSecretSet', 'paypalClientSecretSet', 'razorpayKeySecretSet']
        .forEach((k) => delete payload[k]);
      await api.put('/admin/settings/payment-gateway', payload);
      setStatus({ message: 'Payment gateway settings saved.', severity: 'success' });
      await load();
    } catch (err) {
      setStatus({ message: err.response?.data?.error || err.message, severity: 'error' });
    }
    setSavingPg(false);
  };

  const uploadQrImage = async (file, urlField, setUploading) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPg((prev) => ({ ...prev, [urlField]: data.data.url }));
      setStatus({ message: 'QR code uploaded. Click "Save gateway settings" to apply it.', severity: 'success' });
    } catch (err) {
      setStatus({ message: err.response?.data?.error || 'Failed to upload QR code image.', severity: 'error' });
    }
    setUploading(false);
  };

  const testPaymentGateway = async () => {
    setTestingPg(true);
    setPgResult(null);
    try {
      const { data } = await api.post('/admin/settings/payment-gateway/test-connection', {
        provider: pg.provider
      });
      setPgResult({ ok: true, ...data });
    } catch (err) {
      setPgResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestingPg(false);
      load();
    }
  };

  // ─── AI (Claude API key) handlers ──────────────────────────────────────
  const handleAiChange = (field) => (e) => setAi((prev) => ({ ...prev, [field]: e.target.value }));

  const saveAi = async () => {
    setSavingAi(true);
    setStatus({ message: '', severity: 'info' });
    try {
      if (!ai.claudeApiKey || ai.claudeApiKey.includes('•')) {
        setStatus({ message: 'Enter a new Claude API key to save.', severity: 'error' });
        setSavingAi(false);
        return;
      }
      await api.put('/admin/settings/ai', { claudeApiKey: ai.claudeApiKey });
      setStatus({ message: 'Claude API key saved — takes effect immediately, no restart needed.', severity: 'success' });
      await load();
    } catch (err) {
      setStatus({ message: err.response?.data?.error || err.message, severity: 'error' });
    }
    setSavingAi(false);
  };

  const testAi = async () => {
    setTestingAi(true);
    setAiResult(null);
    try {
      const { data } = await api.post('/admin/settings/ai/test-connection');
      setAiResult({ ok: true, ...data });
    } catch (err) {
      setAiResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestingAi(false);
      load();
    }
  };

  if (loading || !form) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={2}>Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Central configuration for platform email, payment gateway and image search.
      </Typography>

      {status.message && <Alert severity={status.severity} sx={{ mb: 2 }} onClose={() => setStatus({ message: '' })}>{status.message}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<Mail />} iconPosition="start" label="Email" />
          <Tab icon={<CreditCard />} iconPosition="start" label="Payment Gateway" />
          <Tab icon={<SmartToy />} iconPosition="start" label="AI Assistant" />
        </Tabs>
      </Paper>

      {tab === 0 && (
      <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6"><Mail sx={{ verticalAlign: 'middle', mr: 1 }} />Email Settings</Typography>
          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={<Switch checked={form.enabled} onChange={handleChange('enabled')} />}
              label={form.enabled ? 'Enabled' : 'Disabled'}
            />
            <Button size="small" variant="outlined" onClick={applyGmailPreset}>Use Gmail preset</Button>
          </Stack>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Sender Name" value={form.fromName} onChange={handleChange('fromName')}
              helperText="Shown as the sender label, e.g. OSESG" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Sender Email (From)" value={form.fromEmail} onChange={handleChange('fromEmail')}
              helperText="Should match or be an authorised alias of the SMTP user" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Admin Email" value={form.adminEmail} onChange={handleChange('adminEmail')}
              helperText="Receives system alerts and test emails by default" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }}><Chip label="Outgoing (SMTP)" size="small" /></Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP Host" value={form.smtpHost} onChange={handleChange('smtpHost')} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Port" value={form.smtpPort} onChange={handleChange('smtpPort')} /></Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel control={<Switch checked={!!form.smtpSecure} onChange={handleChange('smtpSecure')} />} label="SSL (port 465)" />
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="SMTP Username" value={form.smtpUser} onChange={handleChange('smtpUser')} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="password" label="SMTP Password / App Password" value={form.smtpPass} onChange={handleChange('smtpPass')}
              helperText={form.smtpPassSet ? 'Leave unchanged to keep existing password. For Gmail, use a 16-character App Password.' : 'For Gmail, generate a 16-character App Password.'} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }}><Chip label="Incoming (IMAP)" size="small" /></Divider>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="IMAP Host" value={form.imapHost} onChange={handleChange('imapHost')} /></Grid>
          <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Port" value={form.imapPort} onChange={handleChange('imapPort')} /></Grid>
          <Grid item xs={6} sm={3}>
            <FormControlLabel control={<Switch checked={form.imapSecure !== false} onChange={handleChange('imapSecure')} />} label="SSL/TLS (993)" />
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="IMAP Username" value={form.imapUser} onChange={handleChange('imapUser')} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="password" label="IMAP Password / App Password" value={form.imapPass} onChange={handleChange('imapPass')}
              helperText={form.imapPassSet ? 'Leave unchanged to keep existing password.' : ''} />
          </Grid>
        </Grid>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}><Send sx={{ verticalAlign: 'middle', mr: 1 }} />Test Outgoing Email</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField fullWidth size="small" label="Recipient (optional)" value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} placeholder={form.adminEmail || form.smtpUser} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button fullWidth variant="contained" startIcon={<Send />} disabled={testingOut} onClick={testOutgoing}>
              {testingOut ? 'Sending…' : 'Send test email'}
            </Button>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Last test: {formatDate(form.lastOutgoingTestAt)}{' '}
            {form.lastOutgoingTestAt && (form.lastOutgoingTestOk
              ? <Chip size="small" icon={<CheckCircle />} label="OK" color="success" sx={{ ml: 1 }} />
              : <Chip size="small" icon={<ErrorIcon />} label="Failed" color="error" sx={{ ml: 1 }} />)}
          </Typography>
          {form.lastOutgoingTestError && !form.lastOutgoingTestOk && (
            <Alert severity="error" sx={{ mt: 1 }}>{form.lastOutgoingTestError}</Alert>
          )}
        </Box>

        {outResult && (
          <Alert severity={outResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
            {outResult.ok
              ? `Email sent successfully to ${outResult.to} (id: ${outResult.messageId})`
              : `Failed: ${outResult.error}`}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" mb={1}><Inbox sx={{ verticalAlign: 'middle', mr: 1 }} />Test Incoming Email (IMAP)</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Connects to the configured IMAP inbox and lists the 5 most recent messages to verify that the platform can read replies from customers and suppliers.
        </Typography>
        <Button variant="contained" startIcon={<Inbox />} disabled={testingIn} onClick={testIncoming}>
          {testingIn ? 'Checking inbox…' : 'Check inbox'}
        </Button>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Last test: {formatDate(form.lastIncomingTestAt)}{' '}
            {form.lastIncomingTestAt && (form.lastIncomingTestOk
              ? <Chip size="small" icon={<CheckCircle />} label="OK" color="success" sx={{ ml: 1 }} />
              : <Chip size="small" icon={<ErrorIcon />} label="Failed" color="error" sx={{ ml: 1 }} />)}
          </Typography>
          {form.lastIncomingTestError && !form.lastIncomingTestOk && (
            <Alert severity="error" sx={{ mt: 1 }}>{form.lastIncomingTestError}</Alert>
          )}
        </Box>

        {inResult && inResult.ok && (
          <Accordion sx={{ mt: 2 }} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              Inbox: {inResult.totalMessages} messages ({inResult.unseen} unseen) — showing {inResult.recent?.length || 0} most recent
            </AccordionSummary>
            <AccordionDetails>
              {(inResult.recent || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Inbox is empty.</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inResult.recent.map((m) => (
                      <TableRow key={m.uid}>
                        <TableCell>{m.fromName ? `${m.fromName} <${m.from}>` : m.from}</TableCell>
                        <TableCell>{m.subject}</TableCell>
                        <TableCell>{formatDate(m.date)}</TableCell>
                        <TableCell>{m.seen ? <Chip label="Read" size="small" /> : <Chip label="Unread" color="primary" size="small" />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </AccordionDetails>
          </Accordion>
        )}
        {inResult && !inResult.ok && (
          <Alert severity="error" sx={{ mt: 2 }}>{inResult.error}</Alert>
        )}
      </Paper>
      </>
      )}

      {/* ─── Payment Gateway ─────────────────────────────────────────── */}
      {tab === 1 && pg && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6">
              <CreditCard sx={{ verticalAlign: 'middle', mr: 1 }} />Payment Gateway
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Switch checked={pg.enabled} onChange={handlePgChange('enabled')} />}
                label={pg.enabled ? 'Enabled' : 'Disabled'}
              />
              {pg.lastTestAt && (pg.lastTestOk
                ? <Chip size="small" icon={<CheckCircle />} label={`OK (${pg.lastTestProvider})`} color="success" />
                : <Chip size="small" icon={<ErrorIcon />} label={`Failed (${pg.lastTestProvider})`} color="error" />)}
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Configure the gateway used to capture customer card payments. Secret keys are stored encrypted at rest
            and are never sent back to the browser in plain text.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select label="Provider" value={pg.provider} onChange={handlePgChange('provider')}>
                  <MenuItem value="manual">Manual / Offline (no gateway)</MenuItem>
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="razorpay">Razorpay</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select label="Mode" value={pg.mode} onChange={handlePgChange('mode')}>
                  <MenuItem value="sandbox">Sandbox / Test</MenuItem>
                  <MenuItem value="live">Live / Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Default Currency</InputLabel>
                <Select label="Default Currency" value={pg.currency} onChange={handlePgChange('currency')}>
                  {['USD', 'EUR', 'GBP', 'CNY', 'AED', 'SAR', 'INR'].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {pg.provider === 'stripe' && (<>
            <Divider sx={{ my: 3 }}><Chip label="Stripe credentials" size="small" /></Divider>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Publishable Key (pk_…)"
                  value={pg.stripePublishableKey} onChange={handlePgChange('stripePublishableKey')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="password" label="Secret Key (sk_…)"
                  value={pg.stripeSecretKey} onChange={handlePgChange('stripeSecretKey')}
                  helperText={pg.stripeSecretKeySet ? 'Leave unchanged to keep existing key.' : 'Required to charge cards.'} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="password" label="Webhook Signing Secret (whsec_…)"
                  value={pg.stripeWebhookSecret} onChange={handlePgChange('stripeWebhookSecret')}
                  helperText={pg.stripeWebhookSecretSet ? 'Leave unchanged to keep existing secret.' : 'Optional — needed to verify Stripe webhooks.'} />
              </Grid>
            </Grid>
          </>)}

          {pg.provider === 'paypal' && (<>
            <Divider sx={{ my: 3 }}><Chip label="PayPal credentials" size="small" /></Divider>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Client ID"
                  value={pg.paypalClientId} onChange={handlePgChange('paypalClientId')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="password" label="Client Secret"
                  value={pg.paypalClientSecret} onChange={handlePgChange('paypalClientSecret')}
                  helperText={pg.paypalClientSecretSet ? 'Leave unchanged to keep existing secret.' : ''} />
              </Grid>
            </Grid>
          </>)}

          {pg.provider === 'razorpay' && (<>
            <Divider sx={{ my: 3 }}><Chip label="Razorpay credentials" size="small" /></Divider>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Key ID (rzp_…)"
                  value={pg.razorpayKeyId} onChange={handlePgChange('razorpayKeyId')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="password" label="Key Secret"
                  value={pg.razorpayKeySecret} onChange={handlePgChange('razorpayKeySecret')}
                  helperText={pg.razorpayKeySecretSet ? 'Leave unchanged to keep existing secret.' : ''} />
              </Grid>
            </Grid>
          </>)}

          {pg.provider === 'manual' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Manual mode — the platform won't process card payments automatically. Customers will pay via the
              offline methods you've configured (bank transfer, WeChat Pay / Alipay QR, saved cards, etc.).
            </Alert>
          )}

          <Divider sx={{ my: 3 }}><Chip icon={<QrCode2 />} label="Alipay / WeChat Pay QR codes" size="small" /></Divider>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload OSESG's own Alipay and WeChat Pay receiving QR codes. These are shown to customers at checkout
            so they can scan and pay directly — independent of the provider selected above.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#00A1E9" gutterBottom>Alipay 支付宝</Typography>
                {pg.alipayQrUrl ? (
                  <Box component="img" src={`${ASSET_BASE_URL}${pg.alipayQrUrl}`} alt="Alipay QR"
                    sx={{ maxHeight: 180, maxWidth: '100%', borderRadius: 2, border: '1px solid #e0e0e0', p: 1, mb: 1 }} />
                ) : (
                  <Box sx={{ py: 3 }}>
                    <QrCode2 sx={{ fontSize: 64, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary" display="block">No QR code uploaded yet</Typography>
                  </Box>
                )}
                <TextField fullWidth size="small" label="Account / label shown to customers" sx={{ mt: 1, mb: 1.5 }}
                  value={pg.alipayAccountName} onChange={handlePgChange('alipayAccountName')} />
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button component="label" size="small" variant="outlined" startIcon={<CloudUpload />} disabled={uploadingAlipayQr}>
                    {uploadingAlipayQr ? 'Uploading…' : pg.alipayQrUrl ? 'Replace QR' : 'Upload QR'}
                    <input hidden type="file" accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadQrImage(e.target.files[0], 'alipayQrUrl', setUploadingAlipayQr)} />
                  </Button>
                  {pg.alipayQrUrl && (
                    <Button size="small" color="error" variant="outlined" startIcon={<Delete />}
                      onClick={() => setPg((prev) => ({ ...prev, alipayQrUrl: '' }))}>
                      Remove
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#07C160" gutterBottom>WeChat Pay 微信支付</Typography>
                {pg.wechatQrUrl ? (
                  <Box component="img" src={`${ASSET_BASE_URL}${pg.wechatQrUrl}`} alt="WeChat Pay QR"
                    sx={{ maxHeight: 180, maxWidth: '100%', borderRadius: 2, border: '1px solid #e0e0e0', p: 1, mb: 1 }} />
                ) : (
                  <Box sx={{ py: 3 }}>
                    <QrCode2 sx={{ fontSize: 64, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary" display="block">No QR code uploaded yet</Typography>
                  </Box>
                )}
                <TextField fullWidth size="small" label="Account / label shown to customers" sx={{ mt: 1, mb: 1.5 }}
                  value={pg.wechatAccountName} onChange={handlePgChange('wechatAccountName')} />
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button component="label" size="small" variant="outlined" startIcon={<CloudUpload />} disabled={uploadingWechatQr}>
                    {uploadingWechatQr ? 'Uploading…' : pg.wechatQrUrl ? 'Replace QR' : 'Upload QR'}
                    <input hidden type="file" accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadQrImage(e.target.files[0], 'wechatQrUrl', setUploadingWechatQr)} />
                  </Button>
                  {pg.wechatQrUrl && (
                    <Button size="small" color="error" variant="outlined" startIcon={<Delete />}
                      onClick={() => setPg((prev) => ({ ...prev, wechatQrUrl: '' }))}>
                      Remove
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
            <Button variant="outlined" startIcon={<Bolt />} disabled={testingPg} onClick={testPaymentGateway}>
              {testingPg ? 'Testing…' : 'Test connection'}
            </Button>
            <Button variant="contained" startIcon={<Save />} disabled={savingPg} onClick={savePaymentGateway}>
              {savingPg ? 'Saving…' : 'Save gateway settings'}
            </Button>
          </Stack>

          {pg.lastTestAt && (
            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
              Last test: {formatDate(pg.lastTestAt)} — provider: <strong>{pg.lastTestProvider || '—'}</strong>
              {pg.lastTestError && !pg.lastTestOk && (
                <Alert severity="error" sx={{ mt: 1 }}>{pg.lastTestError}</Alert>
              )}
            </Typography>
          )}

          {pgResult && (
            <Alert severity={pgResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
              {pgResult.ok
                ? <>
                    <strong>{pgResult.provider?.toUpperCase()}</strong>: {pgResult.message}
                    {pgResult.details && (
                      <Box component="pre" sx={{ mt: 1, fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}>
                        {JSON.stringify(pgResult.details, null, 2)}
                      </Box>
                    )}
                  </>
                : <>Failed: {pgResult.error}</>}
            </Alert>
          )}
        </Paper>
      )}

      {/* ─── AI Assistant (Claude API key) ─────────────────────────────── */}
      {tab === 2 && ai && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" mb={1}>
            <SmartToy sx={{ verticalAlign: 'middle', mr: 1 }} />Claude API Key
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Powers Sam (the smart, catalog-grounded assistant) and the Haiku chit-chat gate. Saving a new key here
            takes effect immediately for both — no server restart needed. Leave blank to keep using the
            <code> ANTHROPIC_API_KEY</code> environment variable.
          </Typography>

          <TextField
            fullWidth size="small" type="password" label="Claude API Key (sk-ant-…)"
            value={ai.claudeApiKey} onChange={handleAiChange('claudeApiKey')}
            helperText={ai.claudeApiKeySet ? 'A key is currently saved here — leave unchanged to keep it.' : 'Not set here — currently falling back to the environment variable, if any.'}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" startIcon={<Bolt />} disabled={testingAi} onClick={testAi}>
              {testingAi ? 'Testing…' : 'Test connection'}
            </Button>
            <Button variant="contained" startIcon={<Save />} disabled={savingAi} onClick={saveAi}>
              {savingAi ? 'Saving…' : 'Save Claude API key'}
            </Button>
          </Stack>

          {ai.lastTestAt && (
            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
              Last test: {formatDate(ai.lastTestAt)}{' '}
              {ai.lastTestOk
                ? <Chip size="small" icon={<CheckCircle />} label="OK" color="success" sx={{ ml: 1 }} />
                : <Chip size="small" icon={<ErrorIcon />} label="Failed" color="error" sx={{ ml: 1 }} />}
              {ai.lastTestError && !ai.lastTestOk && (
                <Alert severity="error" sx={{ mt: 1 }}>{ai.lastTestError}</Alert>
              )}
            </Typography>
          )}

          {aiResult && (
            <Alert severity={aiResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
              {aiResult.ok
                ? <>
                    {aiResult.message}
                    {aiResult.details && (
                      <Box component="pre" sx={{ mt: 1, fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}>
                        {JSON.stringify(aiResult.details, null, 2)}
                      </Box>
                    )}
                  </>
                : <>Failed: {aiResult.error}</>}
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AdminSettings;
